import { Icon } from '@chiroup/components/Icon';
import { Icons } from '@chiroup/components/icons';
import React, { useEffect } from 'react';
import { HOW_COLLECTED_FLYOUT as COPY } from '../sandboxCopy';
import FullEhrFeaturePill from './FullEhrFeaturePill';

interface HowCollectedModalProps {
  onClose: () => void;
}

const StepHeading: React.FC<{ index: number; title: string }> = ({ index, title }) => (
  <div className="flex flex-col items-center gap-1.5">
    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
      {index}
    </span>
    <p className="min-h-10 text-center text-sm font-bold leading-tight text-gray-900 dark:text-gray-100">
      {title}
    </p>
  </div>
);

const SkeletonLine: React.FC<{ className?: string }> = ({ className = '' }) => (
  <span className={`block h-1.5 rounded bg-gray-200 dark:bg-dark-gray-600 ${className}`} />
);

/**
 * "How is this already filled out?" three-step workflow visual (Kara,
 * 2026-09-28): appointment booked → Chief Complaint Survey completed →
 * patient checks in with the Subjective ready. Illustrations are CSS-built
 * to the provided reference. "Got it!" returns to the Subjective step.
 */
const HowCollectedModal: React.FC<HowCollectedModalProps> = ({ onClose }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);

    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label={COPY.title}
    >
      <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-[2px]" onClick={onClose} />
      <div className="relative flex max-h-[94vh] w-full sm:max-w-3xl flex-col overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-white p-5 sm:p-7 shadow-2xl dark:bg-dark-gray-800 sm:m-4">
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        >
          ✕
        </button>

        <FullEhrFeaturePill className="self-start" />
        <h3 className="mt-3 text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
          {COPY.title}
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-300">{COPY.subtitle}</p>

        <div className="mt-6 flex flex-col gap-4 md:grid md:grid-cols-[1fr_auto_1fr_auto_1fr] md:items-stretch md:gap-3">
          {/* Step 1: appointment booked */}
          <div className="flex flex-col gap-2.5">
            <StepHeading index={1} title={COPY.steps[0].title} />
            <div className="flex flex-1 rounded-xl border border-gray-200 bg-gray-50/60 p-3 dark:border-dark-gray-600 dark:bg-dark-gray-700/40">
              <div className="w-full rounded-lg border border-gray-200 bg-white p-3 dark:border-dark-gray-600 dark:bg-dark-gray-800">
                <div className="flex items-center gap-2">
                  <Icon icon={Icons.calendar} className="h-4 w-4 shrink-0 text-gray-400" />
                  <SkeletonLine className="w-16" />
                </div>
                <p className="mt-2 text-xs font-semibold text-gray-800 dark:text-gray-100">
                  {COPY.appointmentCard.title}
                </p>
                <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-primary-50 px-2 py-1 text-[10px] font-medium leading-tight text-primary-700 ring-1 ring-inset ring-primary-200 dark:bg-primary-900/30 dark:text-primary-300 dark:ring-primary-800">
                  <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-primary-600 text-white">
                    <Icon icon={Icons.check} className="h-2 w-2" />
                  </span>
                  {COPY.appointmentCard.chip}
                </span>
              </div>
            </div>
            <p className="text-center text-xs text-gray-500 dark:text-gray-400">
              {COPY.steps[0].caption}
            </p>
          </div>

          <span aria-hidden className="hidden md:flex items-center text-xl text-gray-300">
            →
          </span>

          {/* Step 2: survey completed (phone) */}
          <div className="flex flex-col gap-2.5">
            <StepHeading index={2} title={COPY.steps[1].title} />
            <div className="flex flex-1 justify-center rounded-xl border border-gray-200 bg-gray-50/60 p-3 dark:border-dark-gray-600 dark:bg-dark-gray-700/40">
              <div className="w-36 rounded-2xl border-2 border-gray-300 bg-white px-2.5 pb-3 pt-2 dark:border-dark-gray-500 dark:bg-dark-gray-800">
                <p className="text-center text-[10px] font-extrabold tracking-tight text-gray-800 dark:text-gray-100">
                  Chiro<span className="text-primary-600">Up</span>
                </p>
                <p className="mt-2 text-[10px] font-semibold leading-tight text-gray-800 dark:text-gray-100">
                  {COPY.surveyCard.question}
                </p>
                <div className="mt-1.5 space-y-1">
                  {COPY.surveyCard.options.map((option, i) => (
                    <div
                      key={option}
                      className={`flex items-center gap-1.5 rounded-md border px-1.5 py-1 text-[10px] ${
                        i === 0
                          ? 'border-primary-300 bg-primary-50 font-medium text-primary-800 dark:border-primary-800 dark:bg-primary-900/30 dark:text-primary-200'
                          : 'border-gray-200 text-gray-500 dark:border-dark-gray-600 dark:text-gray-400'
                      }`}
                    >
                      {i === 0 ? (
                        <span className="flex h-3 w-3 shrink-0 items-center justify-center rounded-full bg-primary-600 text-white">
                          <Icon icon={Icons.check} className="h-2 w-2" />
                        </span>
                      ) : (
                        <span className="h-3 w-3 shrink-0 rounded-full border border-gray-300 dark:border-dark-gray-500" />
                      )}
                      {option}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <p className="text-center text-xs text-gray-500 dark:text-gray-400">
              {COPY.steps[1].caption}
            </p>
          </div>

          <span aria-hidden className="hidden md:flex items-center text-xl text-gray-300">
            →
          </span>

          {/* Step 3: subjective ready */}
          <div className="flex flex-col gap-2.5">
            <StepHeading index={3} title={COPY.steps[2].title} />
            <div className="flex flex-1 rounded-xl border border-gray-200 bg-gray-50/60 p-3 dark:border-dark-gray-600 dark:bg-dark-gray-700/40">
              <div className="w-full rounded-lg border border-gray-200 bg-white p-3 dark:border-dark-gray-600 dark:bg-dark-gray-800">
                <div className="flex items-center justify-between gap-1">
                  <p className="text-xs font-semibold text-gray-800 dark:text-gray-100">
                    {COPY.subjectiveCard.title}
                  </p>
                  <span className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-1.5 py-0.5 text-[9px] font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                    <span className="flex h-3 w-3 shrink-0 items-center justify-center rounded-full bg-primary-600 text-white">
                      <Icon icon={Icons.check} className="h-2 w-2" />
                    </span>
                    {COPY.subjectiveCard.chip}
                  </span>
                </div>
                <div className="mt-2 space-y-2">
                  {COPY.subjectiveCard.rows.map(row => (
                    <div
                      key={row}
                      className="border-t border-gray-100 pt-1.5 dark:border-dark-gray-700"
                    >
                      <p className="text-[10px] font-medium text-gray-700 dark:text-gray-200">
                        {row}
                      </p>
                      <SkeletonLine className="mt-1 w-4/5" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <p className="text-center text-xs text-gray-500 dark:text-gray-400">
              {COPY.steps[2].caption}
            </p>
          </div>
        </div>

        <div className="mt-5 flex items-start gap-3 rounded-lg bg-primary-50 px-4 py-3 ring-1 ring-inset ring-primary-200/70 dark:bg-primary-900/20 dark:ring-primary-800">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-600 text-white">
            <Icon icon={Icons.clock} className="h-4 w-4" />
          </span>
          <p className="text-sm text-gray-700 dark:text-gray-200">
            <span className="font-semibold text-primary-800 dark:text-primary-200">
              {COPY.bannerLead}
            </span>{' '}
            {COPY.bannerBody}
          </p>
        </div>

        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="w-full sm:w-auto rounded-lg bg-primary-600 px-6 py-3 sm:py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-700"
          >
            {COPY.cta}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HowCollectedModal;
